/**
 * Delivery Fee & Haversine Distance Calculation Utilities
 * Leton Coffee Web Production
 */

export interface DeliveryCalculationResult {
  distanceKm: number;
  fee: number;
  isAllowed: boolean;
  error?: string;
}

/**
 * Full precision Haversine formula to compute great-circle distance between two GPS coordinates in kilometers.
 */
export function calculateHaversineDistanceKm(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  if (
    !Number.isFinite(lat1) ||
    !Number.isFinite(lon1) ||
    !Number.isFinite(lat2) ||
    !Number.isFinite(lon2)
  ) {
    return NaN;
  }

  const R = 6371; // Earth's mean radius in km
  const toRad = (angle: number) => (angle * Math.PI) / 180;

  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Calculates delivery fee based on project specifications:
 * - Distance <= 2 km: Rp5.000
 * - Distance > 2 km up to 15 km: Rp5.000 + Rp2.500 * ceil(distance_km - 2)
 * - Distance > 15 km: Reject checkout (isAllowed = false)
 *
 * Distance is not rounded before calculating fee category.
 */
export function calculateDeliveryFee(distanceKm: number): {
  fee: number;
  isAllowed: boolean;
  error?: string;
} {
  if (!Number.isFinite(distanceKm) || distanceKm < 0) {
    return {
      fee: 0,
      isAllowed: false,
      error: 'Jarak pengantaran tidak valid. Silakan coba deteksi GPS kembali.',
    };
  }

  // Reject distances greater than 15 km
  if (distanceKm > 15) {
    return {
      fee: 0,
      isAllowed: false,
      error: `Jarak pengantaran (${distanceKm.toFixed(1)} km) melebihi batas maksimal 15 km. Silakan pilih opsi Self Pick-Up atau pilih cabang lain.`,
    };
  }

  // Base tier: <= 2 km
  if (distanceKm <= 2) {
    return {
      fee: 5000,
      isAllowed: true,
    };
  }

  // Additional km tier: Rp5.000 + Rp2.500 * ceil(distance - 2)
  const additionalKm = Math.ceil(distanceKm - 2);
  const fee = 5000 + 2500 * additionalKm;

  return {
    fee,
    isAllowed: true,
  };
}

/**
 * Fixed Service Fee (Biaya Layanan Tetap) applied once per transaction across all outlets,
 * customer application, Kiosk Self-Service, and all payment methods (DANA, QRIS, TUNAI).
 */
export const FIXED_SERVICE_FEE = 1500;

export interface OrderBillingBreakdown {
  subtotal: number;
  discountAmount: number;
  subtotalAfterDiscount: number;
  deliveryFee: number;
  serviceFee: number;
  totalAmount: number;
}

/**
 * Authoritative order billing calculation.
 * Guarantees idempotency: always computes totalAmount from components
 * (subtotal - discount + deliveryFee + FIXED_SERVICE_FEE) so retries never double-count the Rp1.500 service fee.
 */
export function calculateAuthoritativeOrderTotal(params: {
  subtotal: number;
  discountAmount?: number;
  orderType?: string;
  deliveryFee?: number;
  serviceFee?: number;
}): OrderBillingBreakdown {
  const rawSubtotal = Math.max(0, Math.round(Number(params.subtotal || 0)));
  const rawDiscount = Math.max(0, Math.round(Number(params.discountAmount || 0)));
  const discountAmount = Math.min(rawSubtotal, rawDiscount);
  const subtotalAfterDiscount = Math.max(0, rawSubtotal - discountAmount);
  const isDelivery = String(params.orderType || '').toUpperCase().trim() === 'DELIVERY';
  const deliveryFee = isDelivery ? Math.max(0, Math.round(Number(params.deliveryFee || 0))) : 0;
  const serviceFee = rawSubtotal > 0 ? FIXED_SERVICE_FEE : 0;
  const totalAmount = subtotalAfterDiscount + deliveryFee + serviceFee;

  return {
    subtotal: rawSubtotal,
    discountAmount,
    subtotalAfterDiscount,
    deliveryFee,
    serviceFee,
    totalAmount,
  };
}

/**
 * Provably resolves the service fee for an existing/persisted order record without retroactively
 * applying Rp1.500 to historical transactions created before the service fee policy.
 *
 * Rules:
 * 1. If items and a positive stored totalAmount are present:
 *    - If storedTotal matches `GREATEST(0, subtotal - discount) + deliveryFee` (within ±1 IDR),
 *      the order provably did NOT include a service fee (historical pre-policy order) -> returns 0.
 *    - If storedTotal matches `GREATEST(0, subtotal - discount) + deliveryFee + FIXED_SERVICE_FEE` (within ±1 IDR),
 *      the order provably includes the Rp1.500 service fee -> returns FIXED_SERVICE_FEE (1500).
 * 2. Otherwise, if `service_fee` / `serviceFee` is explicitly stored as a finite number on the record,
 *    returns `Math.max(0, explicitFee)`.
 * 3. If `service_fee` / `serviceFee` is NULL or undefined (historical pre-policy DB row), returns 0.
 */
export function resolvePersistedOrderServiceFee(record: {
  serviceFee?: number | null;
  service_fee?: number | null;
  totalAmount?: number | null;
  total_amount?: number | null;
  discountAmount?: number | null;
  discount_amount?: number | null;
  deliveryFee?: number | null;
  delivery_fee?: number | null;
  orderType?: string | null;
  order_type?: string | null;
  items?: any[] | null;
} | null | undefined): number {
  if (!record || typeof record !== 'object') return 0;

  const rawExplicit =
    record.service_fee !== undefined && record.service_fee !== null
      ? Number(record.service_fee)
      : record.serviceFee !== undefined && record.serviceFee !== null
      ? Number(record.serviceFee)
      : null;

  const storedTotal = Number(record.total_amount ?? record.totalAmount ?? 0);
  const discount = Math.max(0, Number(record.discount_amount ?? record.discountAmount ?? 0));
  const orderType = String(record.order_type ?? record.orderType ?? '').toUpperCase().trim();
  const isDelivery = orderType === 'DELIVERY';
  const delivery = isDelivery ? Math.max(0, Number(record.delivery_fee ?? record.deliveryFee ?? 0)) : 0;

  const items = Array.isArray(record.items) ? record.items : [];
  const itemsSubtotal = items.reduce((acc, it) => {
    if (!it) return acc;
    const qty = Math.max(1, Number(it.quantity || 1));
    if (Number(it.subtotal || 0) > 0) {
      return acc + Number(it.subtotal);
    }
    const unit = Number(it.unitPrice ?? it.unit_price ?? it.price ?? 0);
    return acc + unit * qty;
  }, 0);

  if (itemsSubtotal > 0 && storedTotal > 0) {
    const baseWithoutServiceFee = Math.max(0, itemsSubtotal - discount) + delivery;
    if (Math.abs(storedTotal - baseWithoutServiceFee) <= 1) {
      return 0;
    }
    if (Math.abs(storedTotal - (baseWithoutServiceFee + FIXED_SERVICE_FEE)) <= 1) {
      return FIXED_SERVICE_FEE;
    }
  }

  if (rawExplicit !== null && Number.isFinite(rawExplicit)) {
    return Math.max(0, rawExplicit);
  }

  return 0;
}


